import type { FixtureBox } from "@rooms/roomsClient";
import type { NormalizedBox } from "@users/staffPhotoClient";

/** Mirrors brian `RoomCameraSet.fixtures` `max_length`. */
export const MAX_FIXTURES = 32;

/** A drag smaller than this on either side is a click, not a box. */
const MIN_DRAG_PX = 4;

/**
 * The still's source size (`RoomStill.width`/`height`, else the image's own),
 * the pixels `camera_fixtures` are written in and the room-phase lane greys.
 */
export interface SourceSize {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max);

/** In the overlay's `0 0 100 100` viewBox, which is stretched over the still. */
export const toDisplay = (box: FixtureBox, size: SourceSize) => {
  const [x1, y1, x2, y2] = box;
  return {
    x: (x1 / size.width) * 100,
    y: (y1 / size.height) * 100,
    width: ((x2 - x1) / size.width) * 100,
    height: ((y2 - y1) / size.height) * 100,
  };
};

/** A box as brian's capture takes it: fractions of the still's width and height. */
export const toNormalized = (
  [x1, y1, x2, y2]: FixtureBox,
  size: SourceSize,
): NormalizedBox => ({
  x1: x1 / size.width,
  y1: y1 / size.height,
  x2: x2 / size.width,
  y2: y2 / size.height,
});

export const fromNormalized = (
  box: NormalizedBox,
  size: SourceSize,
): FixtureBox => [
  box.x1 * size.width,
  box.y1 * size.height,
  box.x2 * size.width,
  box.y2 * size.height,
];

/** A pointer on the overlay, as a source pixel inside the still. */
export const toSource = (
  client: Point,
  rect: Pick<DOMRect, "left" | "top" | "width" | "height">,
  size: SourceSize,
): Point => ({
  x: clamp(((client.x - rect.left) / rect.width) * size.width, size.width),
  y: clamp(((client.y - rect.top) / rect.height) * size.height, size.height),
});

/** brian stores fixtures as floats; a tenth of a pixel keeps small boxes exact. */
const tenth = (value: number) => Math.round(value * 10) / 10;

/** Inside the frame; null when nothing is left with area. */
const settle = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  size: SourceSize,
): FixtureBox | null => {
  const box = [
    tenth(clamp(x1, size.width)),
    tenth(clamp(y1, size.height)),
    tenth(clamp(x2, size.width)),
    tenth(clamp(y2, size.height)),
  ] as const;
  return box[0] < box[2] && box[1] < box[3] ? box : null;
};

export const fromDrag = (
  start: Point,
  end: Point,
  size: SourceSize,
): FixtureBox | null => {
  if (
    Math.abs(end.x - start.x) < MIN_DRAG_PX ||
    Math.abs(end.y - start.y) < MIN_DRAG_PX
  )
    return null;
  return settle(
    Math.min(start.x, end.x),
    Math.min(start.y, end.y),
    Math.max(start.x, end.x),
    Math.max(start.y, end.y),
    size,
  );
};

const sameBox = (a: FixtureBox, b: FixtureBox) => a.every((v, i) => v === b[i]);

/** Unchanged at the cap or for a box already in the mask. */
export const addFixture = (
  fixtures: readonly FixtureBox[],
  box: FixtureBox,
): readonly FixtureBox[] =>
  fixtures.length >= MAX_FIXTURES || fixtures.some((f) => sameBox(f, box))
    ? fixtures
    : [...fixtures, box];

export const removeFixture = (
  fixtures: readonly FixtureBox[],
  index: number,
): readonly FixtureBox[] => fixtures.filter((_, i) => i !== index);

export const sameFixtures = (
  a: readonly FixtureBox[],
  b: readonly FixtureBox[],
) =>
  a.length === b.length &&
  a.every((box, i) => {
    const other = b[i];
    return other !== undefined && sameBox(box, other);
  });

/** brian reads an empty list and null alike; null is its documented "mask off". */
export const savedFixtures = (
  fixtures: readonly FixtureBox[],
): readonly FixtureBox[] | null => (fixtures.length > 0 ? fixtures : null);
